import * as React from 'react';
import * as _ from 'underscore';

import { Messages } from '../../../../../codegen/Localize';
import { FormikTextBox } from '../../../../../commonInfra/components/FormikField';
import { ByteSizeConversionFactor } from '../../../../../commonInfra/utils/SizeFilter';
import { NumberInputRange } from '../../../../../ts/directives/tbRangeInputValidator';
import { useFormikContext } from '../../../../FormikWrapperForStubbing';
import { disabledText, formError, narrowTextFieldStyle } from '../../../../styles/CommonStyles';
import { StorageSectionProps } from './StorageSection';

export const STORAGE_QUOTA_MAX = NumberInputRange.LONG.MAX;
export const STORAGE_BYTES_FIELD = 'settings.storageQuotaBytes';

export const STORAGE_BYTES_FIELD_DEBOUNCE_MS = 1000;

export function StorageBytesField(props: StorageSectionProps) {
  const formik = useFormikContext<StorageSectionProps>();

  // Don't show the error right away until after the user finishes typing
  const setDebouncedFieldTouched = React.useCallback(_.debounce(() => {
    formik.setFieldTouched(STORAGE_BYTES_FIELD, true);
  }, STORAGE_BYTES_FIELD_DEBOUNCE_MS), [formik.setFieldTouched]);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const sizeInBytes = (+e.target.value) * ByteSizeConversionFactor.ONE_GB;
    const sizeError = sizeInBytesValidator(sizeInBytes);
    const value = sizeError === null ? sizeInBytes : e.target.value;
    formik.setFieldValue(STORAGE_BYTES_FIELD, value);
    setDebouncedFieldTouched();
  }, [formik.setFieldValue, setDebouncedFieldTouched]);

  const disabled = !formik.values.settings.storageQuotaEnabled;
  const validate = React.useCallback((value: string) => disabled ? null : sizeInBytesValidator(+value), [disabled]);

  // Explicitly set storageQuotaBytes to undefined when it has error and storageQuotaEnabled change,
  // so that form validation can happen and form saving can be disabled properly.
  React.useEffect(() => {
    const sizeError = sizeInBytesValidator(formik.values.settings.storageQuotaBytes);
    if (sizeError) {
      formik.setFieldValue(STORAGE_BYTES_FIELD, undefined);
    }
  }, [disabled, formik.setFieldValue]);

  const storageBytesError = formik.errors?.settings?.storageQuotaBytes;

  return <>
    <div>
      <FormikTextBox testId={'storage-quota-input'}
                     name={STORAGE_BYTES_FIELD}
                     label={null}
                     aria-label={Messages.storage()}
                     validate={validate}
                     showValidationMessage={false}
                     disableOnSubmit={true}
                     className={narrowTextFieldStyle}
                     value={sizeInGigabytesFormatter(formik.values.settings.storageQuotaBytes) ?? ''}
                     disabled={disabled}
                     maxLength={9}
                     required={true}
                     onChange={handleChange}
      />
      &nbsp;
      <span className={disabled ? disabledText : undefined}>{Messages.gigabyteAbbrev()}</span>
    </div>
    {!disabled && formik.touched?.settings?.storageQuotaBytes && storageBytesError &&
    <div data-tb-test-id={'storage-bytes-error'} className={formError}>
      {storageBytesError}
    </div>}
  </>;
}

function isValidSize(size: number) {
  return !isNaN(size) && size > 0 && isFinite(size);
}

/** Check if a given value is a valid number to use for storage quota */
export const sizeInBytesValidator = (sizeInBytes: number) => {
  if (!isValidSize(sizeInBytes)) {
    return Messages.storageQuotaMustBePositiveNumber();
  }

  if (sizeInBytes > STORAGE_QUOTA_MAX) {
    return Messages.storageQuotaMustBeLessThanMax();
  }

  return null;
};

/** convert from B -> GB */
export const sizeInGigabytesFormatter = (sizeInBytes: any) =>
  isValidSize(sizeInBytes)
    ? parseFloat((sizeInBytes / ByteSizeConversionFactor.ONE_GB).toFixed(6))
    : sizeInBytes;
